import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  DatePicker,
  Segmented,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import {
  selectors,
  updateScheduleDates,
  updateTimeAdvancementType,
} from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import Field from '../../SessionSettingsShared/components/Field';
import { parseScheduleCsv, ScheduleCsvIssue } from './scheduleCsv';

const DAY_FMT = 'YYYY-MM-DD';
const MAX_CSV_BYTES = 1024 * 1024;
const CSV_TEMPLATE_CONTENT = [
  'date',
  '2025-01-20',
  '2025-02-03',
  '2025-02-24',
  '',
].join('\n');
const CSV_TEMPLATE = `data:text/csv;charset=utf-8,${encodeURIComponent(
  CSV_TEMPLATE_CONTENT,
)}`;

type AdvancementMode = 'fixed_intervals' | 'predetermined_intervals';
type ImportPreview = {
  fileName: string;
  dates: string[];
  issues: ScheduleCsvIssue[];
};

/**
 * Blueprint-authoring only (GeneralViewV2 gates it): choose the historical
 * time-advancement mode and, for predetermined_intervals, author the IN-BETWEEN
 * advancement dates. The trading window (card above) stays the sole owner of
 * start/end: the session starts on start_date, lands on each authored date,
 * then makes a final hop onto end_date -- so the picker only allows business
 * days strictly inside the window. Flipping to predetermined is validated
 * server-side (the schedule may still be empty at flip time -- completeness is
 * a launch check); flipping back keeps the authored dates server-side (inert),
 * so re-flipping restores them.
 */
const BlueprintScheduleCard: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const waiting = useSelector(selectors.blueprintsWaiting);

  const isPredetermined =
    session?.time_advancement_type === 'predetermined_intervals';
  const savedDates = (session?.scenario_schedule_dates ?? []).map(
    row => row.date,
  );

  const [draftDates, setDraftDates] = useState<string[]>(savedDates);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importSequence = useRef(0);

  // Re-seed the draft whenever the saved schedule changes (incl. after a save).
  useEffect(() => {
    setDraftDates((session?.scenario_schedule_dates ?? []).map(r => r.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.scenario_schedule_dates]);

  // A preview belongs to one blueprint/window. Discard stale async reads if
  // navigation or the separately saved trading window changes underneath it.
  useEffect(() => {
    importSequence.current += 1;
    setImportPreview(null);
  }, [session?.id, session?.start_date, session?.end_date]);

  const dirty =
    draftDates.length !== savedDates.length ||
    draftDates.some((date, i) => date !== savedDates[i]);

  // Dates must be business days STRICTLY inside the trading window (the window
  // owns start/end; the backend rejects anything else).
  const windowStart = session?.start_date ? dayjs(session.start_date) : null;
  const windowEnd = session?.end_date ? dayjs(session.end_date) : null;
  const windowStartDate = windowStart?.format(DAY_FMT);
  const windowEndDate = windowEnd?.format(DAY_FMT);
  const disabledDate = (d: Dayjs) => {
    if (d.day() === 0 || d.day() === 6) return true;
    if (windowStart && !d.isAfter(windowStart, 'day')) return true;
    if (windowEnd && !d.isBefore(windowEnd, 'day')) return true;
    return false;
  };

  const setImportError = (fileName: string, message: string) => {
    setImportPreview({ fileName, dates: [], issues: [{ message }] });
  };

  const handleCsvFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;

    const sequence = ++importSequence.current;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setImportError(file.name, 'Choose a file with a .csv extension.');
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      setImportError(file.name, 'The CSV must be 1 MB or smaller.');
      return;
    }
    if (!windowStartDate || !windowEndDate) {
      setImportError(
        file.name,
        'Save the historical trading window before importing dates.',
      );
      return;
    }

    try {
      const csv = await file.text();
      if (sequence !== importSequence.current) return;
      const result = parseScheduleCsv(csv, windowStartDate, windowEndDate);
      setImportPreview({ fileName: file.name, ...result });
    } catch (_error) {
      if (sequence !== importSequence.current) return;
      setImportError(file.name, 'The selected CSV could not be read.');
    }
  };

  const visibleIssues = importPreview?.issues.slice(0, 20) ?? [];

  // Fast playback uses its daily/news mode; hide legacy controls without changing saved settings.
  if (session?.execution_engine === 'fast') return null;

  return (
    <SettingsCard
      title="Time advancement"
      className="set3b-blueprint-schedule-card"
    >
      <Field
        label="Mode"
        hint="Fixed intervals advance by the frequency each period; predetermined lands exactly on your hand-picked dates."
      >
        <Segmented
          value={session?.time_advancement_type ?? 'fixed_intervals'}
          disabled={waiting}
          options={[
            { label: 'Fixed intervals', value: 'fixed_intervals' },
            { label: 'Predetermined dates', value: 'predetermined_intervals' },
          ]}
          onChange={value =>
            dispatch(
              updateTimeAdvancementType({
                id: session.id,
                time_advancement_type: value as AdvancementMode,
              }),
            )
          }
        />
      </Field>
      {isPredetermined && (
        <>
          <Field
            label="Advancement dates"
            hint="IN-BETWEEN landing dates (the session starts on the window start, lands on each date, then ends on the window end). Business days strictly inside the trading window; sorted automatically; at least 1 required to launch."
          >
            <DatePicker
              multiple
              // no clear (x) button: the tiny inline clear wipes the whole draft on a mis-click;
              // dates are removed via the tiles below (or by re-clicking them in the panel)
              allowClear={false}
              style={{ width: '100%' }}
              placeholder="Pick as many dates as you want, then close and save"
              maxTagCount={0}
              value={draftDates.map(date => dayjs(date))}
              disabledDate={disabledDate}
              onChange={values =>
                setDraftDates(
                  ((values as Dayjs[] | null) ?? [])
                    .map(value => value.format(DAY_FMT))
                    .sort(),
                )
              }
            />
          </Field>
          <div style={{ marginTop: 8 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              aria-label="Predetermined dates CSV"
              hidden
              onChange={handleCsvFile}
            />
            <Space size="small" wrap>
              <Button
                size="small"
                disabled={waiting || !windowStartDate || !windowEndDate}
                onClick={() => fileInputRef.current?.click()}
              >
                Import CSV
              </Button>
              <Typography.Link
                href={CSV_TEMPLATE}
                download="predetermined-dates-template.csv"
              >
                Download template
              </Typography.Link>
              <Typography.Text type="secondary">
                One date column; YYYY-MM-DD. Save period changes first.
              </Typography.Text>
            </Space>
          </div>
          {importPreview && (
            <Alert
              style={{ marginTop: 8 }}
              type={importPreview.issues.length > 0 ? 'error' : 'success'}
              showIcon
              title={
                importPreview.issues.length > 0
                  ? `${importPreview.fileName} has ${importPreview.issues.length} issue(s)`
                  : `${importPreview.fileName}: ${importPreview.dates.length} valid date(s)`
              }
              description={
                importPreview.issues.length > 0 ? (
                  <>
                    <ul style={{ margin: '4px 0 8px', paddingLeft: 20 }}>
                      {visibleIssues.map((issue, index) => (
                        <li key={`${issue.line ?? 'file'}-${index}`}>
                          {issue.line ? `Line ${issue.line}: ` : ''}
                          {issue.message}
                        </li>
                      ))}
                    </ul>
                    {importPreview.issues.length > visibleIssues.length && (
                      <Typography.Text type="secondary">
                        And {importPreview.issues.length - visibleIssues.length}{' '}
                        more issue(s).
                      </Typography.Text>
                    )}
                    <div style={{ marginTop: 4 }}>
                      <Button
                        size="small"
                        onClick={() => setImportPreview(null)}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Typography.Text>
                      Replace the current {draftDates.length}-date draft with
                      the sorted CSV dates? Nothing is saved yet.
                    </Typography.Text>
                    <div style={{ marginTop: 8 }}>
                      <Space size="small">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => {
                            setDraftDates(importPreview.dates);
                            setImportPreview(null);
                          }}
                        >
                          Replace draft
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setImportPreview(null)}
                        >
                          Cancel
                        </Button>
                      </Space>
                    </div>
                  </>
                )
              }
            />
          )}
          {/* The canonical read display -- the picker is just the selection tool. */}
          <div style={{ marginTop: 8 }}>
            {draftDates.length === 0 ? (
              <Typography.Text type="secondary">
                No dates yet — pick the exact dates the session should land on
                between the window start and end.
              </Typography.Text>
            ) : (
              <>
                <Typography.Text
                  type="secondary"
                  style={{ display: 'block', marginBottom: 4 }}
                >
                  Selected dates ({draftDates.length}):
                </Typography.Text>
                {draftDates.map(date => (
                  <Tag
                    key={date}
                    closable={!waiting}
                    onClose={() =>
                      setDraftDates(prev => prev.filter(d => d !== date))
                    }
                    style={{ marginBottom: 4 }}
                  >
                    {date}
                  </Tag>
                ))}
              </>
            )}
          </div>
          <div style={{ textAlign: 'right', marginTop: 4 }}>
            <Space size="small">
              <Button
                size="small"
                disabled={!dirty || waiting}
                onClick={() => setDraftDates(savedDates)}
              >
                Discard changes
              </Button>
              <Button
                type="primary"
                size="small"
                loading={waiting}
                disabled={!dirty || draftDates.length === 0}
                onClick={() =>
                  dispatch(
                    updateScheduleDates({ id: session.id, dates: draftDates }),
                  )
                }
              >
                Save schedule
              </Button>
            </Space>
          </div>
        </>
      )}
    </SettingsCard>
  );
};

export default BlueprintScheduleCard;
